import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Switch, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Grid, Chip, Snackbar, Alert, FormControlLabel, Radio, RadioGroup,
    FormControl, FormLabel, Autocomplete, CircularProgress, Stack
} from '@mui/material';
import {
    Edit, DragIndicator, ArrowUpward, ArrowDownward, Visibility, VisibilityOff,
    Search, Save, Close, Refresh, PersonAdd, PersonRemove
} from '@mui/icons-material';
import { api_urls } from '../../utils/api-urls';

const HomeSections = () => {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editDialog, setEditDialog] = useState(false);
    const [visibilityDialog, setVisibilityDialog] = useState(false);
    const [currentSection, setCurrentSection] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Visibility dialog states
    const [visibilityMode, setVisibilityMode] = useState('all');
    const [targetUserIds, setTargetUserIds] = useState([]);
    const [userSearch, setUserSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const showMessage = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchSections = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(api_urls + 'api/admin/home-sections/all');
            if (data?.status) setSections(data.results);
        } catch (error) {
            showMessage('Failed to fetch sections', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSections(); }, [fetchSections]);

    // Toggle active/inactive
    const handleToggle = async (id) => {
        try {
            const { data } = await axios.put(api_urls + `api/admin/home-sections/${id}/toggle`);
            if (data?.status) {
                showMessage(data.message);
                fetchSections();
            }
        } catch (error) {
            showMessage('Failed to toggle section', 'error');
        }
    };

    // Move section up/down
    const handleMove = async (index, direction) => {
        const newSections = [...sections];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newSections.length) return;
        [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
        const orderData = newSections.map((s, i) => ({ id: s._id, order: i + 1 }));
        try {
            const { data } = await axios.put(api_urls + 'api/admin/home-sections/order', { sections: orderData });
            if (data?.status) {
                showMessage('Order updated');
                fetchSections();
            }
        } catch (error) {
            showMessage('Failed to update order', 'error');
        }
    };

    // Open edit dialog
    const handleEdit = (section) => {
        setCurrentSection(section);
        setEditForm({
            nameEn: section.nameEn,
            nameHi: section.nameHi,
            subtitleEn: section.subtitleEn,
            subtitleHi: section.subtitleHi,
            icon: section.icon,
            gradientColors: section.gradientColors?.join(', '),
            sectionGroupNameEn: section.sectionGroupNameEn,
            sectionGroupNameHi: section.sectionGroupNameHi,
            sectionGroupEmoji: section.sectionGroupEmoji,
            isComingSoon: section.isComingSoon,
            requiresAuth: section.requiresAuth,
            navigationRoute: section.navigationRoute,
            specialHandler: section.specialHandler
        });
        setEditDialog(true);
    };

    // Save edit
    const handleSaveEdit = async () => {
        try {
            const updateData = {
                ...editForm,
                gradientColors: editForm.gradientColors?.split(',').map(c => c.trim()).filter(c => c),
            };
            const { data } = await axios.put(api_urls + `api/admin/home-sections/${currentSection._id}`, updateData);
            if (data?.status) {
                showMessage('Section updated successfully');
                setEditDialog(false);
                fetchSections();
            }
        } catch (error) {
            showMessage('Failed to update section', 'error');
        }
    };

    // Open visibility dialog
    const handleVisibility = (section) => {
        setCurrentSection(section);
        setVisibilityMode(section.visibilityMode || 'all');
        setTargetUserIds(section.targetUserIds || []);
        setSearchResults([]);
        setUserSearch('');
        setVisibilityDialog(true);
    };

    // Search users
    const handleSearchUsers = async (search) => {
        setUserSearch(search);
        if (search.length < 3) { setSearchResults([]); return; }
        try {
            setSearchLoading(true);
            const { data } = await axios.get(api_urls + `api/admin/home-sections/search-users?search=${search}`);
            if (data?.status) setSearchResults(data.results);
        } catch (error) {
            console.log('Search error:', error);
        } finally {
            setSearchLoading(false);
        }
    };

    // Add user to target list
    const addUserToTarget = (user) => {
        if (!targetUserIds.includes(user._id)) {
            setTargetUserIds([...targetUserIds, user._id]);
        }
    };

    // Remove user from target list
    const removeUserFromTarget = (userId) => {
        setTargetUserIds(targetUserIds.filter(id => id !== userId));
    };

    // Save visibility
    const handleSaveVisibility = async () => {
        try {
            const { data } = await axios.put(api_urls + `api/admin/home-sections/${currentSection._id}/visibility`, {
                visibilityMode,
                targetUserIds
            });
            if (data?.status) {
                showMessage('Visibility updated successfully');
                setVisibilityDialog(false);
                fetchSections();
            }
        } catch (error) {
            showMessage('Failed to update visibility', 'error');
        }
    };

    const getVisibilityChip = (section) => {
        const mode = section.visibilityMode || 'all';
        const count = section.targetUserIds?.length || 0;
        if (mode === 'all') return <Chip label="All Users" size="small" color="success" />;
        if (mode === 'show_specific') return <Chip label={`Show: ${count} users`} size="small" color="info" />;
        if (mode === 'hide_specific') return <Chip label={`Hide: ${count} users`} size="small" color="warning" />;
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                        🏠 Home Page Sections
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage all {sections.length} sections on the app home page - order, visibility, and content
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<Refresh />} onClick={fetchSections} sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Refresh
                </Button>
            </Box>

            {/* Legend */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Visibility Modes:</Typography>
                <Stack direction="row" spacing={2}>
                    <Chip label="All Users - Everyone can see" size="small" color="success" />
                    <Chip label="Show Specific - Only selected users" size="small" color="info" />
                    <Chip label="Hide Specific - Hidden from selected users" size="small" color="warning" />
                </Stack>
            </Paper>

            {/* Sections Table */}
            <Paper sx={{ borderRadius: 3 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f0f4ff' }}>
                                <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 80 }}>Order</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 50 }}>Icon</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Name (EN/HI)</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Group</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Visibility</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 80 }}>Active</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Colors</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 120 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : sections.map((section, index) => (
                                <TableRow key={section._id} hover sx={{
                                    opacity: section.isActive ? 1 : 0.5,
                                    bgcolor: section.isActive ? 'inherit' : '#fafafa'
                                }}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0}>
                                            <IconButton size="small" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                                                <ArrowUpward fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleMove(index, 'down')} disabled={index === sections.length - 1}>
                                                <ArrowDownward fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontSize={24}>{section.icon}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{section.nameEn}</Typography>
                                        <Typography variant="caption" color="text.secondary">{section.nameHi}</Typography>
                                        <br />
                                        <Typography variant="caption" sx={{ color: '#666' }}>{section.subtitleEn}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={section.sectionGroupNameEn} size="small" variant="outlined"
                                            sx={{ fontSize: '0.7rem' }} />
                                    </TableCell>
                                    <TableCell>{getVisibilityChip(section)}</TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={section.isActive}
                                            onChange={() => handleToggle(section._id)}
                                            size="small"
                                            color="success"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {section.gradientColors?.map((c, i) => (
                                                <Box key={i} sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: c, border: '1px solid #ddd' }} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0}>
                                            <Tooltip title="Edit Section">
                                                <IconButton size="small" onClick={() => handleEdit(section)} sx={{ color: '#1976d2' }}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Manage Visibility">
                                                <IconButton size="small" onClick={() => handleVisibility(section)} sx={{ color: '#9c27b0' }}>
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    ✏️ Edit Section: {currentSection?.nameEn}
                    <IconButton onClick={() => setEditDialog(false)} sx={{ ml: 'auto' }}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Name (English)" value={editForm.nameEn || ''} onChange={(e) => setEditForm({ ...editForm, nameEn: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Name (Hindi)" value={editForm.nameHi || ''} onChange={(e) => setEditForm({ ...editForm, nameHi: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Subtitle (English)" value={editForm.subtitleEn || ''} onChange={(e) => setEditForm({ ...editForm, subtitleEn: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Subtitle (Hindi)" value={editForm.subtitleHi || ''} onChange={(e) => setEditForm({ ...editForm, subtitleHi: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth label="Icon (Emoji)" value={editForm.icon || ''} onChange={(e) => setEditForm({ ...editForm, icon: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField fullWidth label="Gradient Colors (comma separated)" value={editForm.gradientColors || ''} onChange={(e) => setEditForm({ ...editForm, gradientColors: e.target.value })} size="small" helperText="e.g. #FF6B00, #FF8C40, #FFB366" />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth label="Group Name (EN)" value={editForm.sectionGroupNameEn || ''} onChange={(e) => setEditForm({ ...editForm, sectionGroupNameEn: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth label="Group Name (HI)" value={editForm.sectionGroupNameHi || ''} onChange={(e) => setEditForm({ ...editForm, sectionGroupNameHi: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField fullWidth label="Group Emoji" value={editForm.sectionGroupEmoji || ''} onChange={(e) => setEditForm({ ...editForm, sectionGroupEmoji: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Navigation Route" value={editForm.navigationRoute || ''} onChange={(e) => setEditForm({ ...editForm, navigationRoute: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Special Handler" value={editForm.specialHandler || ''} onChange={(e) => setEditForm({ ...editForm, specialHandler: e.target.value })} size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel control={<Switch checked={editForm.isComingSoon || false} onChange={(e) => setEditForm({ ...editForm, isComingSoon: e.target.checked })} />} label="Coming Soon Badge" />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControlLabel control={<Switch checked={editForm.requiresAuth !== false} onChange={(e) => setEditForm({ ...editForm, requiresAuth: e.target.checked })} />} label="Requires Login" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditDialog(false)} variant="outlined" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleSaveEdit} variant="contained" startIcon={<Save />} sx={{ textTransform: 'none' }}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Visibility Dialog */}
            <Dialog open={visibilityDialog} onClose={() => setVisibilityDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    👁️ Visibility: {currentSection?.nameEn}
                    <IconButton onClick={() => setVisibilityDialog(false)} sx={{ ml: 'auto' }}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <FormControl component="fieldset" sx={{ mb: 3 }}>
                        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Who can see this section?</FormLabel>
                        <RadioGroup value={visibilityMode} onChange={(e) => setVisibilityMode(e.target.value)}>
                            <FormControlLabel value="all" control={<Radio />}
                                label={<Box><Typography variant="body1" sx={{ fontWeight: 600 }}>All Users</Typography><Typography variant="caption" color="text.secondary">Everyone can see this section</Typography></Box>} />
                            <FormControlLabel value="hide_specific" control={<Radio />}
                                label={<Box><Typography variant="body1" sx={{ fontWeight: 600 }}>Hide from Specific Users</Typography><Typography variant="caption" color="text.secondary">Hide this section from selected users only. Everyone else can see it.</Typography></Box>} />
                            <FormControlLabel value="show_specific" control={<Radio />}
                                label={<Box><Typography variant="body1" sx={{ fontWeight: 600 }}>Show to Specific Users Only</Typography><Typography variant="caption" color="text.secondary">Only selected users can see this section. It will be hidden from everyone else.</Typography></Box>} />
                        </RadioGroup>
                    </FormControl>

                    {visibilityMode !== 'all' && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                {visibilityMode === 'hide_specific' ? '🚫 Users to HIDE this section from:' : '✅ Users to SHOW this section to:'}
                            </Typography>

                            {/* User Search */}
                            <TextField
                                fullWidth size="small" placeholder="Search by name, phone, or email (min 3 chars)..."
                                value={userSearch}
                                onChange={(e) => handleSearchUsers(e.target.value)}
                                InputProps={{ startAdornment: searchLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : <Search sx={{ mr: 1, color: '#999' }} /> }}
                                sx={{ mb: 2 }}
                            />

                            {/* Search Results */}
                            {searchResults.length > 0 && (
                                <Paper variant="outlined" sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                                    {searchResults.map(user => (
                                        <Box key={user._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#f5f8ff' } }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {user.firstName || user.name || "User"} {user.lastName || ""}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    📞 {user.phone} {user.email ? `| ✉️ ${user.email}` : ''}
                                                </Typography>
                                            </Box>
                                            <Button size="small" variant="outlined" startIcon={<PersonAdd />}
                                                onClick={() => addUserToTarget(user)}
                                                disabled={targetUserIds.includes(user._id)}
                                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                                {targetUserIds.includes(user._id) ? 'Added' : 'Add'}
                                            </Button>
                                        </Box>
                                    ))}
                                </Paper>
                            )}

                            {/* Selected Users */}
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Selected Users ({targetUserIds.length}):
                            </Typography>
                            {targetUserIds.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No users selected. Search and add users above.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {targetUserIds.map(uid => (
                                        <Chip key={uid} label={uid.slice(-6)} size="small"
                                            onDelete={() => removeUserFromTarget(uid)}
                                            color={visibilityMode === 'hide_specific' ? 'warning' : 'info'} />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setVisibilityDialog(false)} variant="outlined" sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleSaveVisibility} variant="contained" startIcon={<Save />} sx={{ textTransform: 'none' }}>
                        Save Visibility
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HomeSections;
